import { useState } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { ca } from 'date-fns/locale'
import { IconExternalLink, IconNotebook, IconPlus } from '@tabler/icons-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { caseBeds24IdsForReservation, caseExcerpt, casosPathForBeds24Id } from '@/lib/use-cases'
import { useUseCases } from '@/hooks/useUseCases'
import { TOPIC_LABEL, UseCaseForm } from './UseCaseForm'

interface ReservationUseCasesSectionProps {
  /** `cuid` intern: és el que filtra la llista. */
  reservationId: string
  /** Número de Beds24 de la reserva oberta. Sense ell no es pot escriure cap cas. */
  beds24Id?: string | null
  /** Germanes del grup de Beds24, si en té (`group_beds24_ids` del backend). */
  groupBeds24Ids?: string[]
}

/**
 * Secció "Casos" de la fitxa — `plans/reservation-context-and-use-cases.md` §F3.5.
 *
 * Escriure un cas amb la reserva al davant és tota la gràcia: qui l'escriu no ha de
 * recordar el número, ni el nom de l'hoste, ni què deia el fil. Per això el formulari
 * és el mateix component que a `/casos` (`UseCaseForm`, muntat tal qual) i l'única
 * cosa que hi posa la fitxa són les reserves d'entrada.
 *
 * Aquí s'escriu i es llegeix; a la línia de temps de missatgeria els casos només es
 * llegeixen. Un sol lloc per escriure vol dir un sol lloc on mirar si una cosa s'ha
 * desat.
 */
export function ReservationUseCasesSection({
  reservationId,
  beds24Id,
  groupBeds24Ids = [],
}: ReservationUseCasesSectionProps) {
  const [writing, setWriting] = useState(false)

  const { data, isLoading, error } = useUseCases({ reservationId }, !!reservationId)
  const cases = data?.data ?? []

  const defaultBeds24Ids = caseBeds24IdsForReservation(beds24Id, groupBeds24Ids)
  const canWrite = defaultBeds24Ids.length > 0
  const isGroup = defaultBeds24Ids.length > 1

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          <IconNotebook className="h-4 w-4" />
          Casos
          {cases.length > 0 && <Badge variant="secondary">{cases.length}</Badge>}
        </h3>
        <div className="flex items-center gap-1">
          {beds24Id && (
            <Button variant="ghost" size="sm" asChild className="gap-1 text-xs">
              {/* «A /casos» es llegia com «4 casos» al costat del rètol de buit (A4.5). */}
              <Link
                to={casosPathForBeds24Id(beds24Id)}
                title="Obre la pàgina Casos filtrada per aquesta reserva"
              >
                <IconExternalLink className="h-3.5 w-3.5" />
                Obre a Casos
              </Link>
            </Button>
          )}
          <Button
            size="sm"
            variant={writing ? 'secondary' : 'outline'}
            onClick={() => setWriting((open) => !open)}
            disabled={!canWrite}
            title={
              canWrite
                ? undefined
                : 'Aquesta reserva no té número de Beds24: un cas va lligat sempre a un número'
            }
            className="gap-1"
          >
            <IconPlus className="h-4 w-4" />
            Nou cas
          </Button>
        </div>
      </div>

      {writing && canWrite && (
        <div className="rounded-lg border bg-muted/30 p-3">
          {isGroup && (
            <p className="mb-3 text-xs text-muted-foreground">
              Aquesta reserva forma part d'un grup de Beds24: el cas arrenca lligat a les{' '}
              {defaultBeds24Ids.length} reserves. Treu les que no toquin.
            </p>
          )}
          <UseCaseForm
            defaultBeds24Ids={defaultBeds24Ids}
            onCreated={() => setWriting(false)}
            onCancel={() => setWriting(false)}
          />
        </div>
      )}

      {error ? (
        <p className="text-sm text-red-600">
          No s'han pogut carregar els casos: {(error as Error).message}
        </p>
      ) : isLoading || !data ? (
        // Sense `data` (consulta desactivada o encara no resolta) no se sap si n'hi ha: cap rètol de buit.
        isLoading ? <p className="text-sm text-muted-foreground">Carregant casos…</p> : null
      ) : cases.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Cap cas escrit per aquesta reserva.
          {canWrite && ' Si ha passat res que valgui la pena recordar, escriu-lo ara.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {cases.map((useCase) => (
            <li
              key={useCase.id}
              className={cn(
                'rounded-lg border-l-4 border-l-emerald-400 bg-muted/30 p-3',
                // Un cas tancat no s'amaga: es veu apagat. La traça és el que es volia
                // conservar («ha de persistir encara que es marqui com a resolta»).
                useCase.status === 'done' && 'border-l-muted-foreground/30 opacity-70',
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  {useCase.topic ? (
                    <Badge variant="secondary">{TOPIC_LABEL[useCase.topic]}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">
                      sense tema
                    </Badge>
                  )}
                  {useCase.status === 'done' && (
                    <Badge variant="outline" className="text-muted-foreground">
                      fet
                    </Badge>
                  )}
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {format(new Date(useCase.createdAt), 'dd/MM/yyyy', { locale: ca })}
                </span>
              </div>
              <p className="mt-1.5 text-sm">{caseExcerpt(useCase.whatHappened)}</p>
              <p className="mt-1 text-xs text-muted-foreground">
                {useCase.authorName ?? 'Autor desconegut'}
                {useCase.reservations.length > 1 && ` · ${useCase.reservations.length} reserves`}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
